import React, { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Image as ImageIcon, FileText, Mic, Film, File as FileIcon, Trash2, UploadCloud, Lock,
  Eye, Download, ShieldCheck, History, RotateCcw, Archive, CheckCircle2, Clock, X, Info, Sparkles, Camera, Layers,
  CalendarCheck, CalendarClock, Scale,
} from "lucide-react";
import { api, fileUrl } from "@/lib/apiClient";
import { useGate } from "@/context/GateContext";
import { Button } from "@/components/ui/button";
import UnassignedInbox from "@/components/UnassignedInbox";
import BulkImport from "@/components/BulkImport";
import ArchiveTree from "@/components/ArchiveTree";
import DiscoveryVault from "@/components/DiscoveryVault";
import BulkDateConfirm from "@/components/BulkDateConfirm";
import ReviewCenter from "@/components/ReviewCenter";
import { JusticeThinking } from "@/components/Justice";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export function formatBytes(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(1)} ${u[i]}`;
}

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" }); }
  catch { return iso; }
};

const CATEGORIES = ["Photo", "Screenshot", "Message / Text", "Document", "Court Order", "Medical", "School", "Financial", "Audio", "Video", "Other"];

const FORMAT_ICON = { photo: ImageIcon, pdf: FileText, voice: Mic, video: Film, document: FileText, other: FileIcon };

const ACCEPT = "image/*,application/pdf,audio/*,video/*,.pdf,.jpg,.jpeg,.png,.heic,.heif,.gif,.webp,.bmp,.tiff,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.md,.ppt,.pptx,.mp3,.wav,.m4a,.aac,.ogg,.flac,.mp4,.mov,.avi,.mkv,.m4v,.zip,application/zip";

const CHUNK_THRESHOLD = 10 * 1024 * 1024; // auto-chunk files larger than 10MB
const CHUNK_SIZE = 5 * 1024 * 1024;
const PAGE_SIZE = 30;

const shortId = (id) => `EK-${(id || "").slice(-6).toUpperCase()}`;

// Client-side downscale/compress large phone photos before upload (Canvas API).
// Keeps non-images and small images untouched. Max 2560px longest edge, ~90% JPEG quality.
const MAX_DIM = 2560;
const compressImage = (file) => {
  return new Promise((resolve) => {
    if (!file || !/^image\//.test(file.type || "") || /image\/(gif|heic|heif)/.test(file.type || "")) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      const scale = Math.min(1, MAX_DIM / Math.max(width, height));
      if (scale >= 1) { URL.revokeObjectURL(url); return resolve(file); }
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob || blob.size >= file.size) return resolve(file);
          const name = file.name.replace(/\.(png|webp|bmp|tiff?)$/i, ".jpg");
          resolve(new File([blob], name, { type: "image/jpeg", lastModified: Date.now() }));
        },
        "image/jpeg",
        0.9
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
};

function Badge({ tone = "blue", icon: Icon, children, onClick, testid }) {
  const tones = {
    blue: { color: "var(--ek-secure)", border: "rgba(14,159,110,0.35)", bg: "rgba(14,159,110,0.08)" },
    primary: { color: "var(--ek-primary)", border: "rgba(46,130,246,0.35)", bg: "rgba(46,130,246,0.08)" },
    gold: { color: "#E4B34A", border: "rgba(228,179,74,0.35)", bg: "rgba(228,179,74,0.08)" },
    gray: { color: "var(--ek-text-muted)", border: "var(--ek-border)", bg: "rgba(148,163,184,0.06)" },
    red: { color: "var(--ek-error)", border: "rgba(240,97,109,0.35)", bg: "rgba(240,97,109,0.08)" },
  }[tone];
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-1 px-2.5 py-2 rounded text-[10px] font-medium tracking-wide transition-transform hover:-translate-y-px"
      style={{ color: tones.color, border: `1px solid ${tones.border}`, background: tones.bg, cursor: onClick ? "pointer" : "default" }}>
      {Icon && <Icon className="h-3 w-3" />} {children}
    </button>
  );
}

function EvidenceCard({ item, onDelete, onRestore, onOpenPanel, onOpenHistory, onOpenExtract, onConfirmDate, index, archived }) {
  const Icon = FORMAT_ICON[item.format] || FileIcon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.03 }}
      className="group border h-full flex flex-col relative rounded-lg overflow-hidden"
      style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}
      data-testid={`evidence-card-${item.id}`}
    >
      <div className="h-40 border-b flex items-center justify-center overflow-hidden relative" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
        {item.format === "photo" ? (
          <img src={fileUrl(item.id)} alt={item.filename} className="h-full w-full object-cover" />
        ) : (
          <Icon className="h-10 w-10" style={{ color: "var(--ek-text-muted)" }} strokeWidth={1.2} />
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <span className="ek-mono-label px-1.5 py-0.5 rounded" style={{ background: "rgba(7,11,20,0.7)", color: "var(--ek-text-secondary)" }}>{item.category || "Uncategorized"}</span>
        </div>
        {!archived && (
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2" style={{ background: "rgba(10,11,14,0.72)" }}>
            <a href={fileUrl(item.id)} target="_blank" rel="noreferrer" data-testid={`evidence-view-${item.id}`}>
              <Button size="sm" variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)" }}><Eye className="h-4 w-4 mr-1" /> View</Button>
            </a>
            <a href={fileUrl(item.id)} download={item.filename} data-testid={`evidence-download-${item.id}`}>
              <Button size="sm" variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)" }}><Download className="h-4 w-4" /></Button>
            </a>
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium truncate" title={item.filename} data-private>{item.filename}</span>
          {archived ? (
            <button onClick={() => onRestore(item)} data-testid={`evidence-restore-${item.id}`} className="text-slate-500 hover:text-emerald-400 transition-colors shrink-0" title="Restore"><RotateCcw className="h-4 w-4" /></button>
          ) : (
            <button onClick={() => onDelete(item)} data-testid={`evidence-delete-${item.id}`} className="text-slate-500 hover:text-red-400 transition-colors shrink-0" title="Delete"><Trash2 className="h-4 w-4" /></button>
          )}
        </div>

        {item.format === "voice" && !archived && <audio controls src={fileUrl(item.id)} className="w-full h-8 mt-1" />}
        {item.transcript && (
          <p className="text-xs leading-relaxed line-clamp-2" style={{ color: "var(--ek-text-secondary)" }} data-private>
            <span className="ek-mono-label">TRANSCRIPT</span> · {item.transcript}
          </p>
        )}

        <div className="flex flex-wrap gap-1.5 mt-1">
          {archived ? (
            <Badge tone="gray" icon={Archive} testid={`badge-deleted-${item.id}`}>Deleted · Record Preserved</Badge>
          ) : (
            <>
              <Badge tone="primary" icon={Sparkles} onClick={() => onOpenExtract(item)} testid={`badge-extract-${item.id}`}>Information Found</Badge>
              <Badge tone="blue" icon={Lock} onClick={() => onOpenPanel(item)} testid={`badge-original-${item.id}`}>Original Preserved</Badge>
              <Badge tone="gold" icon={History} onClick={() => onOpenHistory(item)} testid={`badge-history-${item.id}`}>History</Badge>
              {item.date_status === "confirmed" && item.event_date ? (
                <Badge tone="blue" icon={CalendarCheck} onClick={() => onConfirmDate(item)} testid={`badge-date-${item.id}`}>Date: {String(item.event_date).slice(0, 10)}</Badge>
              ) : item.date_review_needed ? (
                <Badge tone="red" icon={CalendarClock} onClick={() => onConfirmDate(item)} testid={`badge-date-review-${item.id}`}>Date Review Needed</Badge>
              ) : (
                <Badge tone="gold" icon={CalendarClock} onClick={() => onConfirmDate(item)} testid={`badge-date-unconfirmed-${item.id}`}>Unconfirmed date</Badge>
              )}
            </>
          )}
        </div>

        <div className="mt-auto pt-2 flex items-center justify-between">
          <span className="ek-mono-label">{formatBytes(item.size)}</span>
          <span className="ek-mono-label flex items-center gap-1" style={{ color: "var(--ek-secure)" }}><Lock className="h-3 w-3" /> AES-256</span>
        </div>
      </div>
    </motion.div>
  );
}

function ConfirmDateDialog({ item, onClose, reload }) {
  const [selected, setSelected] = useState("");
  const [manual, setManual] = useState("");
  const [saving, setSaving] = useState(false);
  const open = !!item;
  const detected = (item?.detected_dates || []);
  const SRC_LABEL = {
    exif_metadata: "EXIF / Metadata", visible_in_image: "Visible in Image",
    document_content: "Document Content", user_entered: "User Entered", user_confirmed: "User Confirmed",
  };

  const save = async () => {
    const chosen = manual || selected;
    if (!chosen) { toast.error("Pick a detected date or enter one"); return; }
    setSaving(true);
    try {
      const source = manual ? "user_entered" : "user_confirmed";
      await api.post(`/evidence/${item.id}/confirm-date`, { event_date: chosen, source });
      toast.success("Event date confirmed");
      onClose();
      setSelected(""); setManual("");
      reload && reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm date"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="confirm-date-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight">Confirm the event date</DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            The <b>event date</b> is when the documented event actually happened — not when the file was uploaded.
            {item?.date_review_needed ? " Multiple dates were detected; select the correct one." : ""}
          </DialogDescription>
        </DialogHeader>

        {detected.length > 0 ? (
          <div className="space-y-2" data-testid="detected-dates-list">
            <p className="ek-mono-label">DETECTED DATES</p>
            {detected.map((d, i) => (
              <button key={i} type="button" data-testid={`detected-date-${i}`}
                onClick={() => { setSelected(d.value); setManual(""); }}
                className="w-full flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors"
                style={{ borderColor: selected === d.value && !manual ? "var(--ek-primary)" : "var(--ek-border)",
                  background: selected === d.value && !manual ? "rgba(46,130,246,0.08)" : "transparent", color: "var(--ek-text)" }}>
                <span>{String(d.value).slice(0, 10)}</span>
                <span className="ek-mono-label" style={{ color: "var(--ek-text-muted)" }}>{SRC_LABEL[d.source] || d.source}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm" style={{ color: "var(--ek-text-muted)" }} data-testid="no-detected-dates">
            No date was detected in this file. Enter the event date below.
          </p>
        )}

        <div className="mt-2">
          <p className="ek-mono-label mb-1">OR ENTER THE DATE MANUALLY</p>
          <input type="date" value={manual} onChange={(e) => { setManual(e.target.value); setSelected(""); }}
            data-testid="manual-date-input"
            className="w-full rounded-md border px-3 py-2 text-sm bg-transparent"
            style={{ borderColor: "var(--ek-border)", color: "var(--ek-text)" }} />
        </div>

        <p className="text-[11px] mt-1" style={{ color: "var(--ek-text-muted)" }}>
          Your originally detected dates and file metadata are always preserved — confirming only records which date you selected as the event date.
        </p>

        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="confirm-date-save-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            {saving ? "Saving…" : "Confirm date"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Vault({ evidence, reload, matterId, matters = [], highlight = null, onHighlightDone }) {
  const { gate } = useGate();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [picked, setPicked] = useState([]);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [progress, setProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [view, setView] = useState("active"); // active | archived
  const [archived, setArchived] = useState([]);
  const [panelItem, setPanelItem] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [extractItem, setExtractItem] = useState(null);
  const [dateItem, setDateItem] = useState(null);
  const [summary, setSummary] = useState(null);
  const [draft, setDraft] = useState(null);
  const [aiLeft, setAiLeft] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [bulkDateOpen, setBulkDateOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [archiveRefresh, setArchiveRefresh] = useState(0);
  const fileRef = useRef(null);
  // On-demand paginated evidence (infinite scroll) — scales to thousands of files.
  const [paged, setPaged] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dateReviewOnly, setDateReviewOnly] = useState(false);
  const skipRef = useRef(0);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);

  const loadPage = useCallback(async (reset) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setPageLoading(true);
    const skip = reset ? 0 : skipRef.current;
    try {
      const params = new URLSearchParams({ skip: String(skip), limit: String(PAGE_SIZE) });
      if (matterId) params.set("matter_id", matterId);
      if (search.trim()) params.set("q", search.trim());
      if (dateReviewOnly) params.set("date_review", "true");
      const { data } = await api.get(`/evidence/page?${params.toString()}`);
      setTotal(data.total);
      setHasMore(data.has_more);
      skipRef.current = skip + data.items.length;
      setPaged((prev) => (reset ? data.items : [...prev, ...data.items]));
    } catch (e) { console.warn("Vault: failed to load evidence page", e); }
    finally { loadingRef.current = false; setPageLoading(false); }
  }, [matterId, search, dateReviewOnly]);

  // Reset + load first page when case or search changes (debounced for search).
  React.useEffect(() => {
    if (view !== "active") return;
    skipRef.current = 0;
    const t = setTimeout(() => loadPage(true), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [matterId, search, view, dateReviewOnly, loadPage]);

  // Auto-load more as the sentinel scrolls into view.
  React.useEffect(() => {
    if (view !== "active" || !hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingRef.current) loadPage(false);
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, view, loadPage]);

  // Jump-and-highlight a specific file when navigated here from "Items to Review".
  React.useEffect(() => {
    if (!highlight) return;
    let tries = 0;
    const tryFocus = () => {
      const el = document.querySelector(`[data-testid="evidence-card-${highlight}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ek-highlight");
        setTimeout(() => el.classList.remove("ek-highlight"), 2600);
        onHighlightDone && onHighlightDone();
        return;
      }
      if (tries++ < 6 && hasMore) { loadPage(false); setTimeout(tryFocus, 500); }
      else { onHighlightDone && onHighlightDone(); }
    };
    const t = setTimeout(tryFocus, 400);
    return () => clearTimeout(t);
  }, [highlight]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    api.get("/ai-usage").then(({ data }) => setAiLeft(data)).catch(() => setAiLeft(null));
  }, [evidence]);

  const addFiles = (list) => setPicked((prev) => [...prev, ...Array.from(list)]);
  const onDrop = useCallback((e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files); }, []);

  const uploadSingle = async (file, onPct) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    if (matterId) fd.append("matter_id", matterId);
    const { data } = await api.post("/evidence", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (ev) => onPct(ev.total ? Math.round((ev.loaded * 100) / ev.total) : 0),
    });
    return data;
  };

  const uploadChunked = async (file, onPct) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const { data: init } = await api.post("/evidence/chunk/init", {
      filename: file.name, content_type: file.type || "application/octet-stream",
      category, matter_id: matterId || "", total_chunks: totalChunks, file_size: file.size,
    });
    const uid = init.upload_id;
    try {
      for (let c = 0; c < totalChunks; c++) {
        const start = c * CHUNK_SIZE;
        const blob = file.slice(start, Math.min(file.size, start + CHUNK_SIZE));
        const cfd = new FormData();
        cfd.append("index", String(c));
        cfd.append("chunk", blob, `${file.name}.part${c}`);
        await api.post(`/evidence/chunk/${uid}`, cfd, { headers: { "Content-Type": "multipart/form-data" } });
        onPct(Math.round(((c + 1) / totalChunks) * 100));
      }
      const { data } = await api.post(`/evidence/chunk/${uid}/complete`);
      return data;
    } catch (e) {
      try { await api.post(`/evidence/chunk/${uid}/abort`); } catch { /* ignore */ }
      throw e;
    }
  };

  const doUpload = async () => {
    if (!gate()) return;
    if (!picked.length) { toast.message("Choose at least one file"); return; }
    setUploading(true);
    let added = 0, fail = 0, firstDoc = null, firstFile = null;
    for (let i = 0; i < picked.length; i++) {
      const f = await compressImage(picked[i]);
      const onPct = (pct) => setProgress((p) => ({ ...p, [i]: pct }));
      try {
        const up = (f.size > CHUNK_THRESHOLD)
          ? await uploadChunked(f, onPct)
          : await uploadSingle(f, onPct);
        added += up?.batch ? (up.added || 0) : 1;
        if (!up?.batch && !firstDoc && /^image\//.test(f.type || "")) { firstDoc = up; firstFile = f; }
        setProgress((p) => ({ ...p, [i]: 100 }));
      } catch (e) {
        fail += 1;
        setProgress((p) => ({ ...p, [i]: -1 }));
        toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    if (added) { if (!(firstDoc && picked.length === 1)) setSummary(added); toast.success(`Successfully added ${added} item${added === 1 ? "" : "s"} to the Evidence Vault`); }
    setUploading(false);
    if (!fail) {
      setOpen(false); setPicked([]); setProgress({});
      if (fileRef.current) fileRef.current.value = "";
    }
    reload();
    loadPage(true);
    if (firstDoc && picked.length === 1) setTimeout(() => setDraft({ doc: firstDoc, file: firstFile }), 350);
  };

  const [delTarget, setDelTarget] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const onDelete = (item) => setDelTarget(item);

  const doArchive = async () => {
    const item = delTarget;
    try { await api.delete(`/evidence/${item.id}`); toast.success("Moved to Archived — original preserved"); reload(); loadPage(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
    finally { setDelTarget(null); }
  };

  const doPermanent = async () => {
    const item = delTarget;
    setDelBusy(true);
    try {
      const { data } = await api.delete(`/evidence/${item.id}?permanent=true`);
      const c = data.cascade || {};
      const bits = [`${c.extractions || 0} extracted fact(s)`];
      if (c.timeline_removed) bits.push(`${c.timeline_removed} timeline entr${c.timeline_removed === 1 ? "y" : "ies"}`);
      if (c.reports_flagged) bits.push(`${c.reports_flagged} report(s) flagged`);
      toast.success(`Permanently deleted — removed ${bits.join(", ")}.`);
      reload(); loadPage(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setDelBusy(false); setDelTarget(null); }
  };

  const loadArchived = async () => {
    try { const { data } = await api.get("/evidence/archived"); setArchived(data); }
    catch { toast.error("Could not load archived records"); }
  };
  const onRestore = async (item) => {
    try { await api.post(`/evidence/${item.id}/restore`); toast.success("Record restored"); await loadArchived(); reload(); loadPage(true); }
    catch { toast.error("Restore failed"); }
  };
  const switchView = async (v) => { setView(v); if (v === "archived") await loadArchived(); };

  const list = view === "active" ? paged : archived;

  return (
    <div>
      {view === "active" && <UnassignedInbox matters={matters} onLinked={reload} />}
      {view === "active" && <ArchiveTree matterId={matterId} refreshKey={archiveRefresh} />}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="ek-mono-label mb-1">THE VAULT</div>
          <h2 className="font-head text-2xl font-medium tracking-tight">Encrypted evidence</h2>
          <p className="text-sm mt-1" style={{ color: "var(--ek-text-secondary)" }}>
            {(view === "active" ? total : archived.length)} {view === "active" ? "active" : "archived"} item{(view === "active" ? total : archived.length) === 1 ? "" : "s"} · originals preserved & hashed
          </p>
          {aiLeft && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs rounded-md border px-2.5 py-1" style={{ borderColor: "var(--ek-border)", color: "var(--ek-text-secondary)" }} data-testid="ai-credits-nudge">
              <Sparkles className="h-3.5 w-3.5" style={{ color: aiLeft.remaining <= 3 ? "var(--ek-warning)" : "var(--ek-primary)" }} />
              {aiLeft.remaining} AI {aiLeft.remaining === 1 ? "analysis" : "analyses"} left this month
              {aiLeft.remaining <= 3 && <span className="font-medium" style={{ color: "var(--ek-primary)" }}>· Upgrade for more</span>}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {view === "active" && (
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files…"
              data-testid="vault-search-input"
              className="rounded-md border px-3 py-1.5 text-sm w-40 sm:w-48"
              style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)", color: "var(--ek-text)" }}
            />
          )}
          {view === "active" && (
            <button
              onClick={() => setDateReviewOnly((v) => !v)}
              data-testid="vault-date-review-filter"
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                borderColor: dateReviewOnly ? "var(--ek-primary)" : "var(--ek-border)",
                background: dateReviewOnly ? "var(--ek-primary)" : "transparent",
                color: dateReviewOnly ? "#fff" : "var(--ek-text-secondary)",
              }}
              title="Show only files that still need a date confirmed"
            >
              <CalendarClock className="h-3.5 w-3.5" /> Needs date review
            </button>
          )}
          <div className="inline-flex rounded-md border overflow-hidden" style={{ borderColor: "var(--ek-border)" }}>
            <button onClick={() => switchView("active")} data-testid="vault-active-toggle" className="px-3 py-1.5 text-xs font-medium transition-colors" style={{ background: view === "active" ? "var(--ek-primary)" : "transparent", color: view === "active" ? "#fff" : "var(--ek-text-secondary)" }}>Active</button>
            <button onClick={() => switchView("archived")} data-testid="vault-archived-toggle" className="px-3 py-1.5 text-xs font-medium transition-colors" style={{ background: view === "archived" ? "var(--ek-steel)" : "transparent", color: view === "archived" ? "#fff" : "var(--ek-text-secondary)" }}>Archived</button>
          </div>

          <Button onClick={() => setBulkOpen(true)} data-testid="bulk-import-btn" variant="outline" className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
            <Layers className="h-4 w-4 mr-2" style={{ color: "var(--ek-primary)" }} /> Bulk import
          </Button>
          <Button onClick={() => setDiscoveryOpen(true)} data-testid="discovery-center-btn" variant="outline" className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
            <Scale className="h-4 w-4 mr-2" style={{ color: "var(--ek-primary)" }} /> Discovery
          </Button>
          <Button onClick={() => setBulkDateOpen(true)} data-testid="bulk-date-btn" variant="outline" className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
            <CalendarClock className="h-4 w-4 mr-2" style={{ color: "var(--ek-primary)" }} /> Confirm dates
          </Button>
          <Button onClick={() => setReviewOpen(true)} data-testid="review-center-btn" variant="outline" className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
            <ShieldCheck className="h-4 w-4 mr-2" style={{ color: "var(--ek-primary)" }} /> Review
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="upload-evidence-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            <UploadCloud className="h-4 w-4 mr-2" /> Add evidence
          </Button>
        </div>
      </div>

      {bulkOpen && (
        <BulkImport
          matterId={matterId}
          onClose={() => setBulkOpen(false)}
          onDone={() => { reload(); loadPage(true); setArchiveRefresh((v) => v + 1); }}
        />
      )}

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setPicked([]); setProgress({}); } }}>
        <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
          <DialogHeader>
            <DialogTitle className="font-head font-medium tracking-tight">Upload to the vault</DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
              PDF, images, documents (DOC/XLS/TXT), audio and video are accepted. Files are AES-256 encrypted and the original is preserved unchanged.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-4">
            <div>
              <label className="ek-mono-label mb-1 block">EVIDENCE CATEGORY</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="evidence-category-select"
                className="w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--ek-page)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              className="border border-dashed rounded-md transition-colors"
              style={{ borderColor: dragActive ? "var(--ek-primary)" : "var(--ek-border)", background: dragActive ? "rgba(46,130,246,0.06)" : "transparent" }}
            >
              <input id="ek-evidence-file" ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="evidence-file-input"
                onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); }} />
              <label htmlFor="ek-evidence-file" role="button" tabIndex={0} data-testid="evidence-upload-dropzone"
                className="block p-8 text-center cursor-pointer select-none">
                <UploadCloud className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--ek-text-muted)" }} strokeWidth={1.2} />
                <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>Drag & drop files here, or tap to choose</p>
                <p className="ek-mono-label mt-2">Large files upload in chunks · up to 200MB each · originals never modified</p>
              </label>
            </div>

            <input id="ek-evidence-cam" type="file" accept="image/*" capture="environment" className="hidden" data-testid="evidence-camera-input"
              onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); }} />
            <label htmlFor="ek-evidence-cam" role="button" tabIndex={0} data-testid="evidence-camera-btn"
              className="mt-3 sm:hidden flex items-center justify-center gap-2 rounded-md border py-3 min-h-[48px] text-sm font-medium cursor-pointer"
              style={{ borderColor: "var(--ek-border)", color: "var(--ek-primary)" }}>
              <Camera className="h-4 w-4" /> Take a photo
            </label>

            {picked.length > 0 && (
              <div className="space-y-2 max-h-44 overflow-auto" data-testid="upload-file-list">
                {picked.map((f, i) => (
                  <div key={i} className="border rounded-md px-3 py-2" style={{ borderColor: "var(--ek-border)" }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs truncate">{f.name}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        {progress[i] === 100 && <CheckCircle2 className="h-3.5 w-3.5" style={{ color: "var(--ek-secure)" }} />}
                        {progress[i] === -1 && <X className="h-3.5 w-3.5" style={{ color: "var(--ek-error)" }} />}
                        {uploading && progress[i] > 0 && progress[i] < 100 && <span className="ek-mono-label" style={{ color: "var(--ek-primary)" }}>{progress[i]}%</span>}
                        <span className="ek-mono-label">{formatBytes(f.size)}</span>
                        {!uploading && <button onClick={() => setPicked((p) => p.filter((_, x) => x !== i))} className="text-slate-500 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>}
                      </div>
                    </div>
                    {uploading && (
                      <div className="h-1 mt-2 rounded-full overflow-hidden" style={{ background: "var(--ek-border)" }}>
                        <div className="h-full transition-all" style={{ width: `${Math.max(0, progress[i] || 0)}%`, background: progress[i] === -1 ? "var(--ek-error)" : "var(--ek-primary)" }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={doUpload} disabled={uploading || !picked.length} data-testid="confirm-upload-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              {uploading ? "ENCRYPTING…" : `Upload securely${picked.length ? ` (${picked.length})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {list.length === 0 ? (
        <div className="border border-dashed p-16 text-center rounded-lg" style={{ borderColor: "var(--ek-border)" }} data-testid="vault-empty">
          {view === "archived" ? <Archive className="h-8 w-8 mx-auto mb-4" style={{ color: "var(--ek-text-muted)" }} strokeWidth={1.2} /> : <Lock className="h-8 w-8 mx-auto mb-4" style={{ color: "var(--ek-text-muted)" }} strokeWidth={1.2} />}
          <p style={{ color: "var(--ek-text-secondary)" }}>{view === "archived" ? "No archived records." : (dateReviewOnly ? "No files need a date confirmed — you're all caught up." : (search ? `No files match “${search}”.` : "Your vault is empty. Add your first piece of evidence."))}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="vault-grid">
            {list.map((item, i) => (
              <EvidenceCard key={item.id} item={item} index={i} archived={view === "archived"}
                onDelete={onDelete} onRestore={onRestore}
                onOpenPanel={setPanelItem} onOpenHistory={setHistoryItem} onOpenExtract={setExtractItem} onConfirmDate={setDateItem} />
            ))}
          </div>
          {view === "active" && (
            <div ref={sentinelRef} className="py-6 text-center">
              {pageLoading && <p className="ek-mono-label" data-testid="vault-loading-more" style={{ color: "var(--ek-text-muted)" }}>Loading more…</p>}
              {!pageLoading && hasMore && (
                <Button variant="outline" onClick={() => loadPage(false)} data-testid="vault-load-more" className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
                  Load more ({total - paged.length} remaining)
                </Button>
              )}
              {!pageLoading && !hasMore && paged.length > 0 && (
                <p className="ek-mono-label" style={{ color: "var(--ek-text-muted)" }}>All {total} items loaded</p>
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={delTarget != null} onOpenChange={(v) => !v && !delBusy && setDelTarget(null)}>
        <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="delete-evidence-dialog">
          <DialogHeader>
            <DialogTitle className="font-head font-semibold flex items-center gap-2" style={{ color: "var(--ek-error)" }}>
              <Trash2 className="h-5 w-5" /> Delete this evidence?
            </DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
              <b style={{ color: "var(--ek-text)" }}>{delTarget?.filename}</b>
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border p-3 text-sm space-y-2" style={{ borderColor: "rgba(239,68,68,0.35)", background: "rgba(239,68,68,0.06)", color: "var(--ek-text-secondary)" }}>
            <p><b style={{ color: "var(--ek-error)" }}>Delete permanently</b> destroys the original file and cannot be undone. It also removes the facts Justice extracted from it, any timeline entries sourced only from this item, and flags reports that may reference it so you can regenerate them.</p>
            <p>Prefer to keep the original for your record? <b style={{ color: "var(--ek-text)" }}>Archive</b> instead — you can restore it anytime.</p>
          </div>
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button onClick={doPermanent} disabled={delBusy} data-testid="confirm-permanent-delete-btn" className="w-full rounded-md" style={{ background: "var(--ek-error)", color: "#fff" }}>
              {delBusy ? "Deleting…" : "Delete permanently"}
            </Button>
            <Button onClick={doArchive} disabled={delBusy} variant="outline" data-testid="confirm-archive-btn" className="w-full rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
              Archive instead (keep original)
            </Button>
            <Button onClick={() => setDelTarget(null)} disabled={delBusy} variant="ghost" data-testid="cancel-delete-btn" className="w-full rounded-md" style={{ color: "var(--ek-text-muted)" }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={summary != null} onOpenChange={(v) => !v && setSummary(null)}>
        <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="upload-summary">
          <DialogHeader>
            <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2"><CheckCircle2 className="h-5 w-5" style={{ color: "var(--ek-secure)" }} /> Upload complete</DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
              Successfully added {summary} item{summary === 1 ? "" : "s"} to the Evidence Vault. Each original was AES-256 encrypted and fingerprinted with a SHA-256 hash.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <RecordIntegrityPanel item={panelItem} onClose={() => setPanelItem(null)} onOpenHistory={(it) => { setPanelItem(null); setHistoryItem(it); }} />
      <ExtractionPanel item={extractItem} onClose={() => setExtractItem(null)} reload={reload} />
      <ConfirmDateDialog item={dateItem} onClose={() => setDateItem(null)} reload={() => { reload(); loadPage(true); }} />
      <DiscoveryVault open={discoveryOpen} onClose={() => setDiscoveryOpen(false)} matterId={matterId} onChanged={() => { reload(); loadPage(true); }} />
      <BulkDateConfirm open={bulkDateOpen} onClose={() => setBulkDateOpen(false)} matterId={matterId} onChanged={() => { reload(); loadPage(true); }} />
      <ReviewCenter open={reviewOpen} onClose={() => setReviewOpen(false)} />
      <DraftRecord draft={draft} matterId={matterId} onClose={() => setDraft(null)} reload={reload} />
      <HistoryDialog item={historyItem} onClose={() => setHistoryItem(null)} />
    </div>
  );
}

function RecordIntegrityPanel({ item, onClose, onOpenHistory }) {
  const [verify, setVerify] = useState(null);
  React.useEffect(() => {
    setVerify(null);
    if (item) api.get(`/evidence/${item.id}/verify`).then(({ data }) => setVerify(data)).catch(() => setVerify({ verified: false }));
  }, [item]);
  if (!item) return null;
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="integrity-panel">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2"><ShieldCheck className="h-5 w-5" style={{ color: "var(--ek-secure)" }} /> Record Integrity</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-1.5">
            <Badge tone="blue" icon={Lock}>Original Preserved</Badge>
            {verify?.verified && <Badge tone="blue" icon={CheckCircle2} testid="badge-verified">Integrity Verified</Badge>}
            {verify && !verify.verified && <Badge tone="red" icon={Info}>Hash mismatch</Badge>}
            <Badge tone="gold" icon={Clock}>History Available</Badge>
          </div>
          <Row label="Source type">{item.source_type || "ORIGINAL"} evidence</Row>
          <Row label="Category">{item.category || "Uncategorized"}</Row>
          <Row label="Version">{item.version || 1}</Row>
          <Row label="Created">{fmtDate(item.created_at)}</Row>
          <Row label="Uploaded by">{item.uploaded_by || "—"}</Row>
          <Row label="Record ID">{shortId(item.id)}</Row>
          <div>
            <div className="ek-mono-label mb-1">SHA-256</div>
            <div className="font-mono text-[10px] break-all p-2 rounded" style={{ background: "var(--ek-page)", color: "var(--ek-text-secondary)" }}>{item.sha256 || "—"}</div>
          </div>
          <p className="text-xs" style={{ color: "var(--ek-text-muted)" }}>
            EvidenceKeeper preserves the original file, its cryptographic hash and a full audit history. This supports — but does not guarantee — the record's authenticity or admissibility.
          </p>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <a href={fileUrl(item.id)} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)" }}><Eye className="h-4 w-4 mr-1" /> View Original</Button></a>
          <Button onClick={() => onOpenHistory(item)} data-testid="panel-view-history-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}><History className="h-4 w-4 mr-1" /> View History</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ item, onClose }) {
  const [events, setEvents] = useState(null);
  React.useEffect(() => {
    setEvents(null);
    if (item) api.get(`/evidence/${item.id}/history`).then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, [item]);
  if (!item) return null;
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2"><History className="h-5 w-5" style={{ color: "#E4B34A" }} /> Record History</DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>{item.filename} · {shortId(item.id)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-80 overflow-auto">
          {events === null && <p className="text-sm" style={{ color: "var(--ek-text-muted)" }}>Loading…</p>}
          {events?.length === 0 && <p className="text-sm" style={{ color: "var(--ek-text-muted)" }}>No history recorded.</p>}
          {events?.map((e, i) => (
            <div key={i} className="flex gap-3">
              <div className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ background: "var(--ek-primary)" }} />
              <div>
                <div className="text-sm font-medium">{e.action}{e.detail ? ` — ${e.detail}` : ""}</div>
                <div className="ek-mono-label">{fmtDate(e.at)}{e.version ? ` · v${e.version}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b pb-2" style={{ borderColor: "var(--ek-border)" }}>
      <span className="ek-mono-label">{label}</span>
      <span className="text-sm text-right" style={{ color: "var(--ek-text)" }} data-private>{children}</span>
    </div>
  );
}

function ExtractionPanel({ item, onClose, reload }) {
  const { gate } = useGate();
  const [ext, setExt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeErr, setAnalyzeErr] = useState("");

  React.useEffect(() => {
    if (!item) return;
    setExt(null); setNote(item.user_context || ""); setLoading(true);
    api.get(`/evidence/${item.id}/extraction`)
      .then(({ data }) => setExt(data))
      .catch(() => setExt(false))
      .finally(() => setLoading(false));
  }, [item]);

  if (!item) return null;

  const analyze = async () => {
    if (!gate()) return;
    setAnalyzing(true); setAnalyzeErr("");
    try {
      const { data } = await api.post(`/evidence/${item.id}/extract`);
      setExt(data);
      toast.success("Justice refined this file");
      reload && reload();
    } catch (e) {
      const msg = e.response?.data?.detail || "Justice couldn't refine this file right now. Please try again.";
      setAnalyzeErr(msg);
      toast.error(msg);
    }
    finally { setAnalyzing(false); }
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.post(`/evidence/${item.id}/context`, { note });
      toast.success("Context saved");
      reload && reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save note"); }
    finally { setSavingNote(false); }
  };

  const fb = (ext && ext.four_box) || {};
  const boxes = [
    { key: "original", label: "RAW / ORIGINAL", desc: "What the file literally shows", tone: "var(--ek-text-secondary)" },
    { key: "fact_only", label: "COURT-ADMISSIBLE FACT EXTRACTION", desc: "Observable facts only", tone: "var(--ek-secure)" },
    { key: "needs_support", label: "NEEDS SUPPORT", desc: "Claims needing corroboration", tone: "#E4B34A" },
    { key: "interpretation", label: "INTERPRETATION", desc: "Subjective / emotional reading", tone: "var(--ek-primary)" },
  ];

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-2xl" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }} data-testid="extraction-panel">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2"><Sparkles className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Information Found</DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>{item.filename} · {shortId(item.id)} · Justice separates observable facts from interpretation. Neutral, no blame.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center ek-mono-label ek-pulse" style={{ color: "var(--ek-text-muted)" }}>LOADING ANALYSIS…</div>
        ) : analyzing && !ext ? (
          <div className="py-10">
            <JusticeThinking size={72} messages={["Justice is reading your evidence…", "Extracting source-documented facts…", "Checking dates and metadata…", "Separating facts from interpretation…"]} testid="fact-filter-processing" />
          </div>
        ) : ext ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              {ext.doc_type && <Badge tone="primary" icon={Info} testid="ext-doctype">{ext.doc_type}</Badge>}
              {(ext.people || []).map((p, i) => <span key={`p${i}`} className="ek-mono-label px-1.5 py-0.5 rounded" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)" }} data-private>{p}</span>)}
              {(ext.dates || []).map((d, i) => <span key={`d${i}`} className="ek-mono-label px-1.5 py-0.5 rounded" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)" }}>{d}</span>)}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="four-box-grid">
              {boxes.map((b) => (
                <div key={b.key} className="rounded-md p-3 border" style={b.key === "fact_only" ? { background: "rgba(46,130,246,0.10)", borderColor: "rgba(46,130,246,0.35)" } : { background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid={`box-${b.key}`}>
                  <div className="ek-mono-label mb-1" style={{ color: b.tone }}>{b.label}</div>
                  <div className="text-[10px] mb-2" style={{ color: "var(--ek-text-muted)" }}>{b.desc}</div>
                  <p className="text-xs leading-relaxed" style={{ color: "var(--ek-text)" }} data-private>{fb[b.key] || "—"}</p>
                </div>
              ))}
            </div>
            {(ext.child_impact || []).length > 0 && (
              <div data-testid="ext-child-impact">
                <div className="ek-mono-label mb-1" style={{ color: "var(--ek-child)" }}>CHILD IMPACT · MOOD / SLEEP / SCHEDULE</div>
                {ext.child_impact.map((c, i) => <p key={`ci${i}`} className="text-xs border-l-2 pl-2 mb-1" style={{ borderColor: "var(--ek-child)", color: "var(--ek-text-secondary)" }} data-private>{c}</p>)}
              </div>
            )}
            {(ext.quotes || []).length > 0 && (
              <div>
                <div className="ek-mono-label mb-1">QUOTES FOUND</div>
                {ext.quotes.map((q, i) => <p key={`q${i}`} className="text-xs italic border-l-2 pl-2 mb-1" style={{ borderColor: "var(--ek-primary)", color: "var(--ek-text-secondary)" }} data-private>“{q}”</p>)}
              </div>
            )}
          </div>
        ) : (
          <div className="py-6 text-center space-y-3">
            <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>This file hasn't been refined yet.</p>
            <Button onClick={analyze} disabled={analyzing} data-testid="run-analysis-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>{analyzing ? "Refining…" : (analyzeErr ? "Try again" : "Refine with Justice · 1 KeeperCredit™")}</Button>
            {analyzeErr && (
              <p className="text-xs px-4" style={{ color: "var(--ek-warning)" }} data-testid="refine-error">{analyzeErr}</p>
            )}
          </div>
        )}

        <div className="pt-2 border-t" style={{ borderColor: "var(--ek-border)" }}>
          <label className="ek-mono-label mb-1 block mt-3">YOUR CONTEXT NOTE (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="context-note-input" placeholder="Explain why this evidence matters…" className="w-full rounded-md px-3 py-2 text-sm resize-none" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }} />
          <div className="flex justify-between items-center mt-2">
            <span className="ek-mono-label" style={{ color: "var(--ek-text-muted)" }}>Included in reports with the source hash.</span>
            <Button onClick={saveNote} disabled={savingNote} data-testid="save-context-btn" size="sm" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>{savingNote ? "Saving…" : "Save note"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DraftRecord({ draft, matterId, onClose, reload }) {
  const [data, setData] = useState(null);
  const [fact, setFact] = useState("");
  const [dateVal, setDateVal] = useState("");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!draft) return;
    setData(null); setFact(""); setDateVal(""); setContext(""); setLoading(true);
    const fd = new FormData(); fd.append("file", draft.file);
    api.post("/coach/ocr-date", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then(({ data }) => { setData(data); setFact(data.summary || data.extracted_text || ""); setDateVal(data.suggested_date || ""); })
      .catch((e) => toast.error(e.response?.data?.detail || "Justice could not read that image"))
      .finally(() => setLoading(false));
  }, [draft]);

  if (!draft) return null;

  const save = async () => {
    if (!fact.trim()) { toast.message("Add a fact summary first"); return; }
    setSaving(true);
    try {
      const pure = context.trim() ? `${fact.trim()}\n\nContext: ${context.trim()}` : fact.trim();
      await api.post("/incidents", { pure_fact: pure, occurred_at: dateVal ? new Date(dateVal).toISOString() : null, evidence_ids: [draft.doc.id], entry_type: "FACTS", matter_id: matterId });
      toast.success("Draft record added to your timeline");
      onClose(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save record"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!draft} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-auto" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }} data-testid="draft-record-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2"><ShieldCheck className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Draft record from evidence</DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>Justice read your upload and drafted a factual record. Review, add context, then save it to this case's timeline.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center ek-mono-label ek-pulse" style={{ color: "var(--ek-text-muted)" }}>JUSTICE IS READING THE DOCUMENT…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="ek-mono-label mb-1 block">DATE</label>
              <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="draft-date-input" className="w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }} />
            </div>
            <div>
              <label className="ek-mono-label mb-1 block">FACT-ONLY DRAFT</label>
              <textarea value={fact} onChange={(e) => setFact(e.target.value)} rows={4} data-testid="draft-fact-input" className="w-full rounded-md px-3 py-2 text-sm resize-none" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }} />
            </div>
            <div>
              <label className="ek-mono-label mb-1 block">ADD CONTEXT (why this matters)</label>
              <textarea value={context} onChange={(e) => setContext(e.target.value)} rows={2} data-testid="draft-context-input" placeholder="Explain the importance of this evidence before Justice finalizes the record…" className="w-full rounded-md px-3 py-2 text-sm resize-none" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={save} disabled={loading || saving || !fact.trim()} data-testid="draft-save-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>{saving ? "Saving…" : "Save to timeline"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
